// Backlog 071-A (simplified, client-side) — Cloud Build progress tracker.
//
// While the deployer's `cloud_build` step is running, the CLI used to show a
// single 5-minute silent spinner. This module fills that gap by talking to
// Cloud Build directly via `gcloud`:
//
//   1. Discover active build IDs by listing `gcloud builds --ongoing` and
//      filtering to those whose source tarball lives under the entity's GCS
//      prefix (`<entitySlug>/<service>/<tag>.tar.gz`).
//   2. For each discovered build, poll `gcloud builds describe --format=json`
//      every ~5s to learn which step is currently running.
//   3. Stream `gcloud builds log <id> --stream` and remember the last
//      non-empty line — that's the live "what is it doing right now" string.
//
// Everything is best-effort. If `gcloud` is missing, denied, or returns
// nothing useful, the tracker stays silent and the existing single-tick
// rendering wins. All shell-outs go through injectable adapters so tests
// never spawn a real `gcloud` process. No API change is required.

export const DEFAULT_GCLOUD_PROJECT = 'dev-atomic';
export const DEFAULT_DESCRIBE_INTERVAL_MS = 5_000;
export const DEFAULT_PROGRESS_LINE_MAX = 80;

// ─────────────────────────────────────────────────────────────────────────
// Pure helpers — parsing + formatting
// ─────────────────────────────────────────────────────────────────────────

export interface ActiveBuild {
  buildId: string;
  serviceName: string;
}

/**
 * Parse the output of `gcloud builds list --ongoing --format=json` and pick
 * out builds whose source tarball lives under `<entitySlug>/`. Returns
 * `[{ buildId, serviceName }]` in input order. Returns `[]` for any malformed
 * input — never throws.
 */
export function parseActiveBuildsFromList(
  rawJson: string,
  entitySlug: string,
): ActiveBuild[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out: ActiveBuild[] = [];
  // GCS object shape: `<entitySlug>/<service>/<tag>.tar.gz` — see
  // packages/api/src/services/build.ts:44. We parse the slug + service from
  // the leading two path components so we don't accidentally match unrelated
  // entities whose slug has ours as a prefix (e.g. `web-platform` vs `web`).
  for (const raw of parsed) {
    if (!raw || typeof raw !== 'object') continue;
    const obj = raw as { id?: unknown; source?: unknown };
    const id = typeof obj.id === 'string' ? obj.id : '';
    if (id.length === 0) continue;
    const src = obj.source as { storageSource?: { object?: unknown } } | null;
    const objectPath =
      src && typeof src === 'object'
        ? src.storageSource?.object
        : undefined;
    if (typeof objectPath !== 'string' || objectPath.length === 0) continue;
    const segments = objectPath.split('/');
    if (segments.length < 2) continue;
    if (segments[0] !== entitySlug) continue;
    out.push({ buildId: id, serviceName: segments[1] });
  }
  return out;
}

export interface BuildStepsSummary {
  currentStepIdx: number | null;
  totalSteps: number;
  currentStepName: string | null;
}

/**
 * Parse the output of `gcloud builds describe <id> --format=json` and pick
 * out the currently-running step. Falls back to the first non-success step
 * when none is explicitly WORKING. Returns null indices when steps are
 * missing, malformed, or all already finished. Never throws.
 */
export function parseBuildStepsFromDescribe(rawJson: string): BuildStepsSummary {
  const empty: BuildStepsSummary = {
    currentStepIdx: null,
    totalSteps: 0,
    currentStepName: null,
  };
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return empty;
  }
  if (!parsed || typeof parsed !== 'object') return empty;
  const stepsRaw = (parsed as { steps?: unknown }).steps;
  if (!Array.isArray(stepsRaw)) return empty;
  const steps = stepsRaw as Array<{ id?: unknown; status?: unknown; args?: unknown }>;

  let workingIdx = -1;
  let firstUnfinishedIdx = -1;
  for (let i = 0; i < steps.length; i++) {
    const status = typeof steps[i]?.status === 'string' ? (steps[i].status as string) : '';
    if (status === 'WORKING') {
      workingIdx = i;
      break;
    }
    if (firstUnfinishedIdx === -1 && status !== 'SUCCESS') {
      firstUnfinishedIdx = i;
    }
  }
  const currentIdx = workingIdx !== -1 ? workingIdx : firstUnfinishedIdx;
  if (currentIdx === -1) {
    return { currentStepIdx: null, totalSteps: steps.length, currentStepName: null };
  }
  const step = steps[currentIdx];
  const id = typeof step?.id === 'string' && step.id.length > 0 ? step.id : null;
  const args = Array.isArray(step?.args) ? (step.args as unknown[]) : [];
  const argName = typeof args[0] === 'string' ? (args[0] as string) : null;
  return {
    currentStepIdx: currentIdx,
    totalSteps: steps.length,
    currentStepName: id ?? argName,
  };
}

/**
 * Render a single dense progress row for display under the cloud_build
 * spinner. Truncates to `maxLen` (default 80) with `...` when overflow.
 *
 * §23 voice: plain, specific, no exclamation marks, no marketing emoji.
 */
export function formatProgressLine(opts: {
  serviceName: string;
  stepLabel: string | null;
  lastLogLine: string | null;
  maxLen?: number;
}): string {
  const maxLen = opts.maxLen ?? DEFAULT_PROGRESS_LINE_MAX;
  const parts = [opts.serviceName];
  if (opts.stepLabel) parts.push(opts.stepLabel);
  const head = parts.join(' ');
  const tail = opts.lastLogLine && opts.lastLogLine.trim().length > 0
    ? '  ' + opts.lastLogLine.trim()
    : '';
  const full = head + tail;
  if (full.length <= maxLen) return full;
  if (maxLen <= 3) return '.'.repeat(maxLen);
  return full.slice(0, maxLen - 3) + '...';
}

// ─────────────────────────────────────────────────────────────────────────
// Adapter signatures
// ─────────────────────────────────────────────────────────────────────────

/** `gcloud builds list --ongoing --format=json`. Returns null on failure. */
export type GcloudListFn = (
  entitySlug: string,
  projectId: string,
) => Promise<string | null>;

/** `gcloud builds describe <id> --format=json`. Returns null on failure. */
export type GcloudDescribeFn = (
  buildId: string,
  projectId: string,
) => Promise<string | null>;

/**
 * `gcloud builds log <id> --stream`. Calls `onLine` for each emitted line
 * (including blank ones — caller filters). Resolves when the stream ends or
 * `signal` aborts. Implementations MUST NOT throw — log streaming is
 * best-effort cosmetic.
 */
export type GcloudStreamLogFn = (
  buildId: string,
  projectId: string,
  onLine: (line: string) => void,
  signal: AbortSignal,
) => Promise<void>;

// ─────────────────────────────────────────────────────────────────────────
// Real adapters
// ─────────────────────────────────────────────────────────────────────────

async function spawnCapture(
  args: string[],
  timeoutMs: number,
): Promise<string | null> {
  const { spawn } = await import('node:child_process');
  return new Promise<string | null>((resolve) => {
    let resolved = false;
    let proc: ReturnType<typeof spawn>;
    try {
      proc = spawn('gcloud', args, { stdio: ['ignore', 'pipe', 'pipe'] });
    } catch {
      resolve(null);
      return;
    }
    let stdout = '';
    proc.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString('utf8');
      if (stdout.length > 256 * 1024) stdout = stdout.slice(-256 * 1024);
    });
    proc.stderr?.on('data', () => { /* discard */ });
    const settle = (out: string | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutHandle);
      resolve(out);
    };
    const timeoutHandle = setTimeout(() => {
      try { proc.kill(); } catch { /* ignore */ }
      settle(null);
    }, timeoutMs);
    proc.on('error', () => settle(null));
    proc.on('close', (code) => {
      if (code === 0 && stdout.length > 0) settle(stdout);
      else settle(null);
    });
  });
}

export const realGcloudList: GcloudListFn = (entitySlug, projectId) =>
  spawnCapture(
    [
      'builds',
      'list',
      '--ongoing',
      `--project=${projectId}`,
      '--format=json',
      `--filter=source.storageSource.object~"^${entitySlug}/"`,
    ],
    10_000,
  );

export const realGcloudDescribe: GcloudDescribeFn = (buildId, projectId) =>
  spawnCapture(
    ['builds', 'describe', buildId, `--project=${projectId}`, '--format=json'],
    10_000,
  );

export const realGcloudStreamLog: GcloudStreamLogFn = async (
  buildId,
  projectId,
  onLine,
  signal,
) => {
  const { spawn } = await import('node:child_process');
  await new Promise<void>((resolve) => {
    let proc: ReturnType<typeof spawn>;
    try {
      proc = spawn(
        'gcloud',
        ['builds', 'log', buildId, '--stream', `--project=${projectId}`],
        { stdio: ['ignore', 'pipe', 'pipe'] },
      );
    } catch {
      resolve();
      return;
    }
    let buffered = '';
    const flushLines = (final = false) => {
      const parts = buffered.split(/\r?\n/);
      // Last element may be a partial line; keep it unless we're final-flushing.
      buffered = final ? '' : (parts.pop() ?? '');
      for (const line of parts) {
        try { onLine(line); } catch { /* ignore */ }
      }
    };
    proc.stdout?.on('data', (chunk: Buffer) => {
      buffered += chunk.toString('utf8');
      flushLines();
    });
    proc.stderr?.on('data', () => { /* discard */ });
    // Hard cap as a safety net: if gcloud hangs (network blip, reauth prompt)
    // and the AbortSignal never fires, this kills the zombie process. 12 min
    // is generous enough not to cut off a real build.
    const safetyHandle = setTimeout(() => {
      try { proc.kill(); } catch { /* ignore */ }
    }, 12 * 60 * 1000);
    const cleanup = () => {
      clearTimeout(safetyHandle);
      flushLines(true);
      resolve();
    };
    proc.on('error', cleanup);
    proc.on('close', cleanup);
    signal.addEventListener('abort', () => {
      try { proc.kill(); } catch { /* ignore */ }
    });
  });
};

// ─────────────────────────────────────────────────────────────────────────
// Tracker
// ─────────────────────────────────────────────────────────────────────────

export interface BuildProgressDeps {
  gcloudList?: GcloudListFn;
  gcloudDescribe?: GcloudDescribeFn;
  gcloudStreamLog?: GcloudStreamLogFn;
  projectId?: string;
  describeIntervalMs?: number;
  /** Per-row truncation cap. Defaults to 80. */
  lineMaxLen?: number;
}

interface BuildState {
  serviceName: string;
  buildId: string;
  currentStepIdx: number | null;
  totalSteps: number;
  currentStepName: string | null;
  lastLogLine: string | null;
}

/**
 * Coordinates Cloud Build discovery + per-build polling. Construct once per
 * `grid plug` invocation; call `start(entitySlug)` when the deployer's
 * `cloud_build` step enters in_progress, and `stop()` when it leaves. Read
 * `getMessages()` from the renderer's per-frame redraw.
 */
export class BuildProgressTracker {
  private readonly deps: Required<Omit<BuildProgressDeps, 'lineMaxLen'>> & {
    lineMaxLen: number;
  };
  private readonly states = new Map<string, BuildState>();
  private readonly intervals: ReturnType<typeof setInterval>[] = [];
  private readonly abortController = new AbortController();
  private stopped = false;

  constructor(deps: BuildProgressDeps = {}) {
    this.deps = {
      gcloudList: deps.gcloudList ?? realGcloudList,
      gcloudDescribe: deps.gcloudDescribe ?? realGcloudDescribe,
      gcloudStreamLog: deps.gcloudStreamLog ?? realGcloudStreamLog,
      projectId: deps.projectId ?? DEFAULT_GCLOUD_PROJECT,
      describeIntervalMs: deps.describeIntervalMs ?? DEFAULT_DESCRIBE_INTERVAL_MS,
      lineMaxLen: deps.lineMaxLen ?? DEFAULT_PROGRESS_LINE_MAX,
    };
  }

  async start(entitySlug: string): Promise<void> {
    if (this.stopped) return;
    let raw: string | null;
    try {
      raw = await this.deps.gcloudList(entitySlug, this.deps.projectId);
    } catch {
      raw = null;
    }
    if (this.stopped) return;
    if (!raw) return;
    const builds = parseActiveBuildsFromList(raw, entitySlug);
    for (const b of builds) {
      this.states.set(b.buildId, {
        serviceName: b.serviceName,
        buildId: b.buildId,
        currentStepIdx: null,
        totalSteps: 0,
        currentStepName: null,
        lastLogLine: null,
      });
      this.kickDescribePoll(b.buildId);
      this.kickLogStream(b.buildId);
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.abortController.abort();
    for (const t of this.intervals) clearInterval(t);
    this.intervals.length = 0;
    this.states.clear();
  }

  getMessages(): Record<string, string[]> {
    if (this.states.size === 0) return {};
    const lines: string[] = [];
    for (const s of this.states.values()) {
      const stepLabel =
        s.currentStepIdx !== null && s.totalSteps > 0
          ? `Step ${s.currentStepIdx + 1}/${s.totalSteps}${
              s.currentStepName ? ' ' + s.currentStepName : ''
            }`
          : null;
      lines.push(
        formatProgressLine({
          serviceName: s.serviceName,
          stepLabel,
          lastLogLine: s.lastLogLine,
          maxLen: this.deps.lineMaxLen,
        }),
      );
    }
    return { cloud_build: lines };
  }

  private kickDescribePoll(buildId: string): void {
    const tick = async (): Promise<void> => {
      if (this.stopped) return;
      let raw: string | null;
      try {
        raw = await this.deps.gcloudDescribe(buildId, this.deps.projectId);
      } catch {
        return;
      }
      if (this.stopped || !raw) return;
      const summary = parseBuildStepsFromDescribe(raw);
      const s = this.states.get(buildId);
      if (!s) return;
      s.currentStepIdx = summary.currentStepIdx;
      s.totalSteps = summary.totalSteps;
      s.currentStepName = summary.currentStepName;
    };
    // Fire-and-forget initial tick; don't await so callers aren't blocked.
    void tick();
    this.intervals.push(setInterval(() => { void tick(); }, this.deps.describeIntervalMs));
  }

  private kickLogStream(buildId: string): void {
    const onLine = (line: string): void => {
      if (this.stopped) return;
      const trimmed = line.trim();
      if (trimmed.length === 0) return;
      const s = this.states.get(buildId);
      if (s) s.lastLogLine = trimmed;
    };
    this.deps
      .gcloudStreamLog(buildId, this.deps.projectId, onLine, this.abortController.signal)
      .catch(() => { /* swallow — best-effort cosmetic */ });
  }
}
